import {
  GetEmailResponse,
  SaveDraftRequest,
  SaveDraftResponse,
  SendEmailResponse,
  ContactSuggestion,
  ContactSuggestionResponse,
} from '@dreamai/types';
import api from '../../../../../api';

/**
 * Fetch email data from the API
 */
export const fetchEmailData = async (
  emailId: string,
  getAccessTokenSilently: () => Promise<string>,
): Promise<GetEmailResponse> => {
  const token = await getAccessTokenSilently();
  const response = await api.get<GetEmailResponse>(
    `/agent-email-storage/${emailId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return response.data;
};

/**
 * Save draft email
 */
export const saveDraftEmail = async (
  emailId: string,
  draftData: SaveDraftRequest,
  getAccessTokenSilently: () => Promise<string>,
): Promise<SaveDraftResponse> => {
  const token = await getAccessTokenSilently();

  const response = await api.put<SaveDraftResponse>(
    `/agent-email-storage/${emailId}/save-draft`,
    draftData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );
  return response.data;
};

/**
 * Send draft email
 */
export const sendDraftEmail = async (
  emailId: string,
  getAccessTokenSilently: () => Promise<string>,
): Promise<SendEmailResponse> => {
  const token = await getAccessTokenSilently();

  const response = await api.post<SendEmailResponse>(
    `/agent-email-storage/${emailId}/send`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );
  return response.data;
};

/**
 * Fetch contact suggestions for email recipients
 * @param emailId - The ID of the email
 * @param query - The search query string
 * @param getAccessTokenSilently - Function to get the access token
 * @returns Promise with contact suggestions
 */
export const fetchContactSuggestions = async (
  emailId: string,
  query: string,
  getAccessTokenSilently: () => Promise<string>,
): Promise<ContactSuggestion[]> => {
  try {
    if (!query || query.trim() === '') {
      return [];
    }

    // Get the access token
    const token = await getAccessTokenSilently();

    const response = await api.get(
      `/agent-email-storage/${emailId}/contact-suggestions?query=${encodeURIComponent(query)}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    const data: ContactSuggestionResponse = response.data;

    if (!data.success || !data.contacts) {
      console.error('Error fetching contact suggestions:', data.error);
      return [];
    }

    return data.contacts;
  } catch (error) {
    console.error('Error fetching contact suggestions:', error);
    return [];
  }
};

/**
 * Download email attachment
 */
export const downloadAttachment = async (
  emailId: string,
  attachmentId: string,
  getAccessTokenSilently: () => Promise<string>,
  requestedFilename?: string,
): Promise<void> => {
  const token = await getAccessTokenSilently();

  // Gmail attachment IDs are not stable across fetches; the backend uses the
  // filename as a fallback to locate the attachment on the re-fetched message.
  const response = await api.get(
    `/agent-email-storage/download-attachment/${emailId}/${attachmentId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: requestedFilename ? { filename: requestedFilename } : undefined,
      responseType: 'blob', // Important for binary data
    },
  );

  // Get the filename from the Content-Disposition header if available
  const contentDisposition = response.headers['content-disposition'];
  let filename = 'attachment';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="(.+)"/i);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1];
    }
  }

  // Create a download link from the blob response
  const blob = new Blob([response.data], {
    type: response.headers['content-type'],
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();

  // Clean up
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};
